// FirstVisitTips
//
// A dismissible tip banner that appears the first time a user lands on a
// specific page. Unlike the spotlight-based GuidedTour (which is fragile
// across mobile, sticky headers, and route changes), this is a plain
// banner that anchors visually to the top of the page content. Easier to
// position correctly on mobile, and a single click to dismiss forever.
//
// Usage:
//
//   <FirstVisitTips
//     pageKey="matters"
//     title="Your case files live here"
//     tips={[
//       'Click "New case file" to open one — every matter has its own tasks, time, docs, billing.',
//       'Use the quick filters (🔥 Urgent, 👤 Mine) to focus your day.',
//       'Press ⌘K from anywhere to jump straight to a matter by name.',
//     ]}
//   />
//
// Dismissal is per-page and stored in localStorage. Once dismissed, the
// banner never returns for that page for that browser. Clearing browser
// data brings them back, by design.

import React, { useEffect, useState } from 'react';
import { Lightbulb, X } from 'lucide-react';

const lsKey = (pageKey) => `lf_tips_${pageKey || 'unknown'}_v1`;

export default function FirstVisitTips({ pageKey, title, tips = [] }) {
  const [dismissed, setDismissed] = useState(true); // start true to avoid flash; hydrate below

  useEffect(() => {
    if (!pageKey) return;
    try {
      const flag = localStorage.getItem(lsKey(pageKey));
      setDismissed(flag === '1');
    } catch {
      setDismissed(true);
    }
  }, [pageKey]);

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(lsKey(pageKey), '1'); } catch { /* ignore */ }
  };

  if (dismissed || !tips.length) return null;

  return (
    <div className="relative rounded-2xl border border-indigo-200 dark:border-indigo-800 bg-gradient-to-br from-indigo-50 via-white to-violet-50 dark:from-indigo-900/20 dark:via-slate-900 dark:to-violet-900/20 p-4 sm:p-5">
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-white/50 dark:hover:bg-slate-800/50 transition"
        aria-label="Dismiss tips"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3 pr-8">
        <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center">
          <Lightbulb size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          {title && (
            <p className="text-sm font-semibold text-slate-900 dark:text-white">
              {title}
            </p>
          )}
          <ul className="mt-2 space-y-1.5">
            {tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-xs sm:text-sm text-slate-700 dark:text-slate-200">
                <span className="flex-shrink-0 inline-block w-1.5 h-1.5 mt-1.5 rounded-full bg-indigo-500" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={dismiss}
            className="mt-3 text-xs font-medium text-indigo-700 dark:text-indigo-300 hover:underline"
          >
            Got it — don't show again
          </button>
        </div>
      </div>
    </div>
  );
}
